/**
 * Factory functions for creating new Concepts, Relations, Diagrams.
 *
 * UUIDs are generated via the WHATWG `crypto.randomUUID()` available in modern
 * browsers, Node, and the Workers runtime — no external dep needed.
 */
import type { BilingualText, Concept, Diagram, Relation, ViewState } from "./types";

export interface CreateConceptInput {
  typeId: string;
  name: BilingualText;
  ownerEmail: string;
  description?: BilingualText | undefined;
  attributes?: Record<string, unknown> | undefined;
  tags?: string[] | undefined;
  parentId?: string | null | undefined;
  ownerTeamId?: string | null | undefined;
  extendsTypeId?: string | null | undefined;
  experimentalInBranch?: string | null | undefined;
  /** Stereotype-ids (M5+). Default lege array — geen stereotypes. */
  stereotypes?: string[] | undefined;
  /**
   * Alternatieve labels per view-resolver-id (M5-§3). Bv.
   * `{ sharp: { nl: "Klant", en: "Customer" } }`.
   */
  altLabels?: Record<string, BilingualText> | undefined;
}

export function createConcept(input: CreateConceptInput): Concept {
  return {
    id: crypto.randomUUID(),
    typeId: input.typeId,
    extendsTypeId: input.extendsTypeId ?? null,
    name: { ...input.name },
    description: input.description ? { ...input.description } : {},
    attributes: input.attributes ? { ...input.attributes } : {},
    tags: input.tags ? [...input.tags] : [],
    ownerEmail: input.ownerEmail,
    ownerTeamId: input.ownerTeamId ?? null,
    parentId: input.parentId ?? null,
    version: 1,
    experimentalInBranch: input.experimentalInBranch ?? null,
    tombstoneInBranch: null,
    stereotypes: input.stereotypes ? [...input.stereotypes] : [],
    altLabels: input.altLabels ? { ...input.altLabels } : {},
  };
}

export interface CreateRelationInput {
  typeId: string;
  sourceConceptId: string;
  targetConceptId: string;
  ownerEmail: string;
  label?: BilingualText | undefined;
  attributes?: Record<string, unknown> | undefined;
  ownerTeamId?: string | null | undefined;
  experimentalInBranch?: string | null | undefined;
  /** Stereotype-ids (M5+). Default lege array — geen stereotypes. */
  stereotypes?: string[] | undefined;
  /** Alternatieve labels per view-resolver-id (M5-§3). */
  altLabels?: Record<string, BilingualText> | undefined;
}

export function createRelation(input: CreateRelationInput): Relation {
  return {
    id: crypto.randomUUID(),
    typeId: input.typeId,
    sourceConceptId: input.sourceConceptId,
    targetConceptId: input.targetConceptId,
    label: input.label ? { ...input.label } : {},
    attributes: input.attributes ? { ...input.attributes } : {},
    ownerEmail: input.ownerEmail,
    ownerTeamId: input.ownerTeamId ?? null,
    version: 1,
    experimentalInBranch: input.experimentalInBranch ?? null,
    tombstoneInBranch: null,
    stereotypes: input.stereotypes ? [...input.stereotypes] : [],
    altLabels: input.altLabels ? { ...input.altLabels } : {},
  };
}

export interface CreateDiagramInput {
  name: BilingualText;
  /** Optioneel bij het aanmaken. Zonder dit veld viel een meegestuurde
   *  beschrijving stil weg en moest 'ie in een tweede aanroep gezet. */
  description?: BilingualText | undefined;
  notationPack: string;
  viewKind: string;
  viewState?: ViewState | undefined;
  attributes?: Record<string, unknown> | undefined;
}

export function createDiagram(input: CreateDiagramInput): Diagram {
  return {
    id: crypto.randomUUID(),
    name: { ...input.name },
    notationPack: input.notationPack,
    viewKind: input.viewKind,
    viewState: input.viewState ? { ...input.viewState } : {},
    attributes: input.attributes ? { ...input.attributes } : {},
    stereotypes: [],
    ...(input.description ? { description: { ...input.description } } : {}),
  };
}
